import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { getStoredApiUrl, isAllowedApiUrl, saveRuntimeApiUrl } from '../api/config';
import { buildLogoutUrl, getCognitoConfig } from '../auth/cognitoConfig';
import { BILLABLE_LOCK_EVENT } from '../hooks/useBillableNavigationLock';

function Panel({ children }: { children: ReactNode }) {
  return (
    <main className="main-content" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <div className="panel" style={{ width: 'min(520px, 100%)' }}>{children}</div>
    </main>
  );
}

/**
 * Single sign-on gate. Renders the app once the operator has a valid Cognito
 * session (obtained via Google), otherwise shows a "Sign in with Google" screen.
 */
function SsoGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [apiUrl, setApiUrl] = useState(getStoredApiUrl());
  const [apiUrlReady, setApiUrlReady] = useState(() => isAllowedApiUrl(getStoredApiUrl()));
  const [urlError, setUrlError] = useState('');
  const [billableLocked, setBillableLocked] = useState(false);

  useEffect(() => {
    const handleBillableLock = (event: Event) =>
      setBillableLocked(Boolean((event as CustomEvent<boolean>).detail));
    window.addEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
    return () => window.removeEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
  }, []);

  const handleSaveUrl = (event: FormEvent) => {
    event.preventDefault();
    const normalized = apiUrl.trim().replace(/\/+$/, '');
    if (!isAllowedApiUrl(normalized)) {
      setUrlError(
        import.meta.env.DEV
          ? 'Enter a valid HTTP or HTTPS Worker URL, or leave it blank for the local proxy.'
          : 'Enter a valid HTTPS Worker URL.'
      );
      return;
    }
    try {
      saveRuntimeApiUrl(normalized);
    } catch {
      setUrlError('Session storage is unavailable. Enable it for this site and try again.');
      return;
    }
    setUrlError('');
    setApiUrlReady(true);
  };

  const handleSignIn = () => {
    setUrlError('');
    void auth.signinRedirect();
  };

  const handleSignOut = () => {
    const config = getCognitoConfig();
    const logoutUrl = config ? buildLogoutUrl(config) : null;
    void auth.removeUser().finally(() => {
      if (logoutUrl) window.location.assign(logoutUrl);
    });
  };

  if (auth.isLoading) {
    return (
      <Panel>
        <h1 style={{ fontSize: '1.5rem' }}>Signing you in…</h1>
      </Panel>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Panel>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Sign in</h1>
        <p style={{ color: 'var(--aged-mahogany-light)', marginBottom: '24px' }}>
          Use your Google Workspace account to access the shipping console.
        </p>

        {!apiUrlReady && (
          <form onSubmit={handleSaveUrl}>
            <div className="form-group">
              <label htmlFor="sso-api-url">Worker API URL *</label>
              <input
                id="sso-api-url"
                type="url"
                value={apiUrl}
                onChange={(event) => setApiUrl(event.target.value)}
                placeholder={import.meta.env.DEV ? 'Same-origin local proxy' : 'https://your-worker.workers.dev'}
                required={!import.meta.env.DEV}
                autoComplete="url"
              />
            </div>
            <div className="action-buttons" style={{ marginBottom: '16px' }}>
              <button type="submit" className="btn btn-ghost">Save API URL</button>
            </div>
          </form>
        )}

        {(auth.error || urlError) && (
          <div role="alert" style={{ color: 'var(--error-text)', marginBottom: '16px' }}>
            {urlError || auth.error?.message}
          </div>
        )}

        <div className="action-buttons">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSignIn}
            disabled={!apiUrlReady}
          >
            Sign in with Google
          </button>
        </div>
      </Panel>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={billableLocked}
        title={billableLocked ? 'Sign-out is locked while a billable request is processing.' : undefined}
        className="btn btn-ghost"
        onClick={handleSignOut}
        style={{ position: 'fixed', right: '12px', bottom: '12px', zIndex: 900, fontSize: '0.8rem' }}
      >
        Sign out
      </button>
      {children}
    </>
  );
}

export default SsoGate;
